import { useState, useEffect } from "react";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

type SegwayType = {
  latitude: number | null;
  longitude: number | null;
  mobility_state_id: number;
  qrcode: string;
  body_number: string;
  port_name: string;
};

type PortType = {
  port_lat: number;
  port_lng: number;
  port_name: string;
  now_number: number;
};

interface MarkerProps {
  map: google.maps.Map | null;
  segway: SegwayType[] | null;
  port: PortType[] | null;
  showMarkers: boolean;
  segway_state_id?: number;
}

const getMarkerIcon = (
  stateId?: number,
  type: "segway" | "port" = "segway"
) => {
  if (type === "port") {
    return null;
  }
  switch (stateId) {
    case 1:
      return require("../../img/custom-marker-P.png");
    case 2:
    case 3:
      return require("../../img/ride.png");
    case 4:
      return require("../../img/custom-marker-not.png");
    default:
      return null;
  }
};

const Marker: React.FC<MarkerProps> = ({ map, segway, port, showMarkers }) => {
  const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
  const [markerCluster, setMarkerCluster] = useState<MarkerClusterer | null>(
    null
  );
  const infoWindow = new google.maps.InfoWindow({ maxWidth: 200 });

  const createInfoWindowContent = (
    item: SegwayType | PortType,
    type: "segway" | "port"
  ) => {
    if (type === "segway") {
      const segwayItem = item as SegwayType;
      return `<div>
                <span>QRコード: ${segwayItem.qrcode}</span><br/>
                <span>ナンバー: ${segwayItem.body_number}</span><br/>
                <span>ポート: ${segwayItem.port_name}</span>
              </div>`;
    } else if (type === "port") {
      const portItem = item as PortType;
      return `<div>
                <span>ポート名: ${portItem.port_name}</span><br />
                <span>駐車台数 : ${portItem.now_number}</span>
              </div>`;
    } else {
      return `<div>情報がありません</div>`;
    };
  };

  const createMarkers = () => {
    const newMarkers = [
      ...(segway
        ? segway.filter(
          item => item.latitude != null && item.longitude != null).map((item) => {
  
            const marker = new google.maps.Marker({
              position: { lat: item.latitude!, lng: item.longitude! },
              map: map!,
              icon: getMarkerIcon(item.mobility_state_id),
            });
            marker.addListener("click", () => {
              if (marker.getPosition()) {
                map!.panTo(marker.getPosition()!);
                const content = createInfoWindowContent(item, "segway");
                infoWindow.setContent(content);
                infoWindow.open({ map: map!, anchor: marker });
              }
            });
            return marker;
          })
        : []),
      ...(port
        ? port.filter(item => item.port_lat != null && item.port_lng != null).map((item) => {
            const marker = new google.maps.Marker({
              position: { lat: item.port_lat, lng: item.port_lng },
              map: map!,
              icon: getMarkerIcon(undefined, "port"),
            });
            marker.addListener("click", () => {
              if (marker.getPosition()) {
                map!.panTo(marker.getPosition()!);
                const content = createInfoWindowContent(item, "port");
                infoWindow.setContent(content);
                infoWindow.open({ map: map!, anchor: marker });
              }
            });
            return marker;
          })
        : []),
    ];
    return newMarkers;
  };

  const clearMarkers = () => {
    markers.forEach((marker) => {
      marker.setMap(null);
    });
    setMarkers([]);
  };

  useEffect(() => {
    if (map && (segway || port)) {
      clearMarkers();
      const newMarkers = createMarkers();
      setMarkers(newMarkers);

      if (markerCluster) {
        markerCluster.clearMarkers();
      }

      const segwayMarkers = newMarkers.filter(
        (marker) => marker.getIcon() !== null
      );

      const newMarkerCluster = new MarkerClusterer({
        map: map,
        markers: segwayMarkers,
      });

      setMarkerCluster(newMarkerCluster);
    }
  }, [map, segway, port]);

  useEffect(() => {
    if (map && port) {
      markers.forEach((marker) => {
        if (marker.getIcon() === null) { 
          if (showMarkers) {
            marker.setMap(map);
          } else {
            marker.setMap(null);
          }
        }
      });
    }
  }, [map, showMarkers, markers]);

  return null;
};

export default Marker;
